import { LayoutElement, LayoutRenderingPurpose, PapyrusComponent, RenderingConfiguration } from "../types";
import { generate } from "../util/guid";
import { sortElementsByLayersWithIndex, elementFactory } from "../CommandContext";

export interface SVGLayoutProps {
    configuration: RenderingConfiguration;
    preserveAspectRatio?: string;
    outlineArea?: {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        scale?: number;
        hidden?: boolean; // When set the editable area will not be stroke. The mask will still take effect.
    };
    viewBox?: { x: number; y: number; width: number; height: number };
    width: number | string;
    height: number | string;
    position?: string;
    maxWidth?: string;
    maxHeight?: string;
    elements: LayoutElement[];
    backgroundColor?: string;
    outlineColor?: string;
    omitBoundClipping?: boolean;
    borderRadius?: number;
}

export const SVGLayout: PapyrusComponent<SVGLayoutProps> = ({
    backgroundColor,
    outlineColor,
    borderRadius,
    configuration,
    elements,
    height,
    maxHeight,
    maxWidth,
    outlineArea,
    position,
    preserveAspectRatio,
    viewBox,
    width,
}) => {
    const rx = borderRadius || 0;
    const strokeScale = 2 * (outlineArea?.scale || 1);
    const calculatedViewbox = viewBox || { x: 0, y: 0, width, height };
    const viewboxString = `${calculatedViewbox.x} ${calculatedViewbox.y} ${calculatedViewbox.width} ${calculatedViewbox.height}`;
    const maskId = generate();

    const defs = configuration.purpose === LayoutRenderingPurpose.FreeDesign && (
        <defs>
            <clipPath id="viewboxClip">
                <rect width={calculatedViewbox.width} height={calculatedViewbox.height} rx={rx} />
            </clipPath>
        </defs>
    );

    const colorProfileElements = configuration.colorProfiles?.map((c, index) => {
        // Color profiles have no typescript definition. We tell the compiler to ignore their lack of existance.
        return (
            /* @ts-ignore */
            <color-profile key={index} name={c.name} xlinkHref={c.key}>
                {" "}
                {/* @ts-ignore */}
            </color-profile>
        );
    });

    // Mixin the rendering configuration if required
    const elementsWithConfiguration = elements.map((el) => {
        return {
            ...el,
            _renderingConfiguration: configuration,
            mask: outlineArea ? `url(#viewmask-${maskId})` : undefined,
        };
    });

    // Order the elements based on layer & index
    const sortedElements = sortElementsByLayersWithIndex(elementsWithConfiguration);

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            xmlSpace="preserve"
            version="1.1"
            preserveAspectRatio={preserveAspectRatio}
            width={width}
            height={height}
            style={{
                gridColumn: 1,
                gridRow: 1,
                maxWidth,
                maxHeight,
                position: position as any,
            }}
            viewBox={viewboxString}
        >
            {colorProfileElements}
            {defs}
            {!!backgroundColor && (
                <rect
                    id="layout-background"
                    width={calculatedViewbox.width}
                    height={calculatedViewbox.height}
                    fill={backgroundColor}
                    rx={rx}
                />
            )}
            <g
                id="element-group"
                clipPath={configuration.purpose === LayoutRenderingPurpose.FreeDesign ? "url(#viewboxClip)" : undefined}
            >
                {sortedElements.map((el) => elementFactory(el)).filter((el) => !!el)}
            </g>
            {outlineArea && (
                <rect
                    x={outlineArea.x}
                    y={outlineArea.y}
                    width={outlineArea.width}
                    height={outlineArea.height}
                    fill="none"
                    stroke={outlineArea.hidden ? "none" : outlineColor ? outlineColor : "#aaaaaa"}
                    strokeWidth={strokeScale / 2}
                    strokeDasharray={`${strokeScale * 2} ${strokeScale}`}
                />
            )}
            {outlineArea && (
                <mask id={`viewmask-${maskId}`}>
                    <rect
                        x={calculatedViewbox.x}
                        y={calculatedViewbox.y}
                        width={calculatedViewbox.width}
                        height={calculatedViewbox.height}
                        fill="black"
                    />
                    <rect
                        x={outlineArea.x}
                        y={outlineArea.y}
                        width={outlineArea.width}
                        height={outlineArea.height}
                        fill="white"
                    />
                </mask>
            )}
        </svg>
    );
};
